import { observable, computed, action, toJS } from 'mobx';
import AppDialog from '../models/AppDialog';
import StrapiHelper from '../services/StrapiHelper.service';

export default class AppDialogStore {
  userStore: any;

  @observable
  queue: AppDialog[] = [];

  @observable
  currentDialog?: AppDialog;

  @observable
  slideIndex: number = 0;

  @action
  addUserStore(store: any) {
    this.userStore = store;
  }

  getSeenDialogs(): number[] {
    try {
      return toJS(this.userStore?.currentUser?.seenDialogs) || [];
    } catch (e) {
      return [];
    }
  }

  isEligible(dialog: AppDialog, seen: number[]): boolean {
    const now = Date.now();
    if (dialog.showAfter && now < new Date(dialog.showAfter).getTime()) {
      return false;
    }
    if (dialog.showUntil && now > new Date(dialog.showUntil).getTime()) {
      return false;
    }
    if (dialog.frequency === 'ONCE_PER_USER' && seen.includes(dialog.id)) {
      return false;
    }
    if (!dialog.slides || dialog.slides.length === 0) {
      return false;
    }
    return true;
  }

  @action
  advance() {
    this.slideIndex = 0;
    this.queue.shift();
    this.currentDialog = this.queue.length > 0 ? this.queue[0] : undefined;
  }

  @action
  async dismissDialog() {
    const dialog = this.currentDialog;
    if (!dialog) return;

    if (dialog.frequency === 'ONCE_PER_USER') {
      const seen = this.getSeenDialogs();
      if (!seen.includes(dialog.id)) {
        seen.push(dialog.id);
        try {
          await this.userStore.setSeenDialogs(seen);
        } catch (e) {
          // non-fatal: dismiss locally even if persistence fails
        }
      }
    }

    this.advance();
  }

  @action
  showNextSlide() {
    if (!this.currentDialog) return;
    if (this.slideIndex < this.currentDialog.slides.length - 1) {
      this.slideIndex++;
    } else {
      this.dismissDialog();
    }
  }

  @action
  showPreviousSlide() {
    if (this.slideIndex > 0) {
      this.slideIndex--;
    }
  }

  @action
  setSlideIndex(idx: number) {
    this.slideIndex = idx;
  }

  @action
  async loadAppDialogs() {
    try {
      const res = await StrapiHelper.loadAppDialogs();
      const dialogs = (res.data as AppDialog[]) || [];
      const seen = this.getSeenDialogs();
      this.queue = dialogs.filter((d) => this.isEligible(d, seen));
      this.slideIndex = 0;
      this.currentDialog = this.queue.length > 0 ? this.queue[0] : undefined;
    } catch (err) {
      // App dialogs are non-essential. Stay silent on failure (missing
      // endpoint, missing permission, transient network) — never toast.
      console.warn('[AppDialogStore] loadAppDialogs failed', err);
    }
  }
}
